<template>
  <div>
    <section class="list" :class="$config.project">
      <h2 class="h4 list-title">
        {{ $config.project === 'lms' ? 'Courses Included' : `${track.name} Career Track Courses ` }}
      </h2>
      <div class="track-long-description tracking-section" data-event="content.scroll" v-html="track.longDescription" />
      <div class="holder">
        <div class="track-line">
          <div class="track-line-filler" />
        </div>
        <div class="courses tracking-section" data-event="courseslist.scroll">
          <article v-for="(course, i) in track.courses" :key="course.slug">
            <CourseCard
              v-if="course.slug"
              :course="course"
              horizontal
              track
              :force-progress="!!track.enrolled"
              url-mode="track-library"
              heading="h3"
            >
              <Dropdown
                v-if="course.type === 'Elective' && $config.project === 'lms'"
                slot="elective"
                :radio-buttons="{ ...electiveCourses, [course.id]: course.name }"
                :selected="String(course.id)"
                :disabled="track.attempted > 0"
                selected-title="Select an elective course"
                light
                class="elective-selector"
                @change="(c) => $emit('elective', { courseId: c, trackCourseId: course.trackCourseId })"
              />
            </CourseCard>
            <div v-else :key="course.slug" class="course-elective">
              <div class="image">
                <img
                  :src="require(`../assets/images/${$config.platform === 'fa' ? 'fa/fa-' : ''}course-elective-placeholder.png`)"
                  :alt="course.name"
                  class="course-image"
                >
              </div>
              <div class="label" :class="`label-${course.type.toLowerCase()}`">
                {{ course.type }}
              </div>
              <div class="info">
                <Dropdown
                  :radio-buttons="electiveCourses"
                  :disabled="track.attempted > 0 || $route.name === 'business-library-slug'"
                  selected-title="Select an elective course"
                  light
                  class="elective-selector"
                  @change="(c) => $emit('elective', { courseId: c, trackCourseId: course.trackCourseId })"
                />
                <p v-if="$route.name === 'business-library-slug'" class="body-2">
                  Elective courses.
                </p>
                <p v-else class="body-2">
                  Please select an elective course to finish your career track and unlock the final exam to recieve your certificate of achievement.
                </p>
              </div>
            </div>
            <div class="track-dot">
              <span>{{ i + 1 }}</span>
            </div>
          </article>
        </div>
        <CertificateBadge class="track-badge" />
      </div>
    </section>
    <div class="footer">
      <p class="h5">
        {{ footerText }}
      </p>
      <p class="body-2" v-html="footerDesc" />
      <nuxt-link
        v-if="$store.getters['auth/user'].role === 'business-admin' && $route.name === 'business-library-slug'"
        :to="`/business/tasks/new/?track=${track.id}`" class="btn-primary btn-lg"
      >
        Assign
      </nuxt-link>
      <nuxt-link
        v-else-if="$config.project === 'site'" to="/signup/" class="btn-primary btn-lg"
        data-event="start_your_career_2.click"
      >
        Start Your Career
      </nuxt-link>
      <template v-else>
        <nuxt-link
          v-if="track.certificateNumber && track.enrolled"
          :to="`/certificates/${track.certificateNumber}/`" class="btn-primary btn-lg"
        >
          Career track certificate
        </nuxt-link>
        <nuxt-link
          v-else-if="track.finalExam && track.enrolled"
          :to="`/exams/?tab=track&trackId=${track.id}`" class="btn-primary btn-lg"
        >
          Final exam
        </nuxt-link>
        <button v-else-if="!track.enrolled" class="btn-primary btn-lg" @click="$emit('enroll')">
          Enroll Now
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import CourseCard from '../Courses/Card';
import CertificateBadge from '../assets/svg/certificate-badge.svg?vue-component';
import Dropdown from '../components/Dropdown';

export default {
  components: {
    CourseCard, CertificateBadge, Dropdown,
  },
  props: {
    track: { type: Object, default: () => {}, required: true },
  },
  computed: {
    electiveCourses() {
      if (this.track.electiveCourses) {
        return this.track.electiveCourses.filter((e) => !this.track.courses.find((c) => c.id === e.id)).reduce((a, key) => Object.assign(a, { [key.id]: key.name }), {});
      }
      return [];
    },
    footerText() {
      let text = this.$config.platform === 'ds' ? `Finish the Track and Become a Certified ${this.track.name}` : `Finish the ${this.track.name} Career Track and Secure a Certificate`;

      if (this.track.certificateNumber && this.$config.project === 'lms') {
        text = 'Congrats! You finished this career track.';
      } else if (this.$config.project === 'lms') {
        text = 'Pass Exams and Get a Career Track Certificate';
      } else if (this.$config.platform === 'ds' && this.track.slug === 'data-scientist') {
        text = 'Finish the Data Scientist Career Track and Get Certified';
      } else if (this.$config.platform === 'ds' && this.track.slug === 'data-analyst') {
        text = 'Finish the Track and Become a Certified Data Analyst';
      } else if (this.$config.platform === 'ds' && this.track.slug === 'business-analyst') {
        text = 'Finish the Track and Become a Certified Business Analyst';
      } else if (this.$config.platform === 'ds' && this.track.slug === 'ai-engineer') {
        text = 'Finish the Track and Become a Certified AI Engineer';
      }

      return text;
    },
    footerDesc() {
      let text = `Once you acquire the certificates successfully, you will possess the necessary skills for a job as a ${this.track.name.toLowerCase()}, and qualifying for a position will be one step closer.`;

      if (this.track.certificateNumber && this.$config.project === 'lms') {
        text = 'Get your career track certificate from here';
      } else if (this.$config.project === 'lms') {
        text = `Start your career as a ${this.track.name.toLowerCase()}`;
      } else if (this.$config.platform === 'ds' && this.track.slug === 'data-scientist') {
        text = 'Acquire a data scientist certificate to prove you possess the necessary knowledge for the job. Build a data science projects portfolio to stand out with applicable skills and get hired.';
      } else if (this.$config.platform === 'ds' && this.track.slug === 'data-analyst') {
        text = 'Acquire your data analyst certificate to move one step closer to landing a job. Build a data analyst project portfolio to showcase your applicable skills and get hired.';
      } else if (this.$config.platform === 'ds' && this.track.slug === 'business-analyst') {
        text = 'Acquire your business analyst certificate to prove you have the necessary knowledge for the job. Build a business analyst projects portfolio to showcase your applicable skills and get hired.';
      } else if (this.$config.platform === 'ds' && this.track.slug === 'ai-engineer') {
        text = 'Earn your AI engineer certificate to demonstrate your expertise to potential employers. Throughout the AI Engineer Career Track, you’ll build a portfolio of AI projects that showcase your practical skills in implementing AI solutions.';
      }

      return text;
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollAnimation();
    });
    window.addEventListener('scroll', this.scrollAnimation);
    this.$once('hook:beforeDestroy', () => window.removeEventListener('scroll', this.scrollAnimation));
  },
  methods: {
    scrollAnimation() {
      const dots = this.$el.querySelectorAll('.track-dot');
      const line = this.$el.querySelector('.track-line');
      const lineFiller = this.$el.querySelector('.track-line .track-line-filler');
      const badge = this.$el.querySelector('.track-badge');
      const badgeBound = badge.getBoundingClientRect();
      let lineHeight = (line.getBoundingClientRect().top - window.innerHeight / 2) * -1;
      const scrolledToBottom = window.innerHeight + (document.documentElement.scrollTop || document.body.scrollTop) >= document.body.scrollHeight;

      if (lineHeight < 0) {
        lineHeight = 0;
      } else if (scrolledToBottom) {
        lineHeight = line.getBoundingClientRect().height;
      }

      dots.forEach((dot, i) => {
        setTimeout(() => {
          if ((lineHeight > dot.offsetParent?.offsetTop && i === 0) || (lineHeight >= dot.offsetParent?.offsetTop && i > 0)) {
            dot.classList.add('filled');
          } else {
            dot.classList.remove('filled');
          }
        }, 300);
      });

      setTimeout(() => {
        lineFiller.style.height = `${lineHeight}px`;
      }, 200);

      if (badgeBound.top - window.innerHeight / 2 < 0 || scrolledToBottom) {
        setTimeout(() => {
          badge.classList.add('filled');
        }, 500);
      } else {
        badge.classList.remove('filled');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.list {
  margin-top: var(--spacing-60);

  .holder {
    position: relative;
    padding-bottom: var(--spacing-50);
    margin-bottom: 10px;
  }

  .courses {
    margin-top: var(--spacing-36);

    article {
      padding-left: 64px;
      position: relative;

      .track-dot {
        position: absolute;
        top: 0;
        left: 0;

        &:before {
          content: '';
          display: block;
          position: absolute;
          top: -10px;
          left: -10px;
          width: 48px;
          height: 48px;
          background: var(--main-light-shade-1);
          z-index: 0;
        }

        span {
          position: relative;
          display: block;
          border-radius: 50%;
          width: 28px;
          height: 28px;
          text-align: center;
          font-size: 18px;
          line-height: 28px;
          transition: all var(--duration) ease-in;
          background-color: var(--main-light-shade-3);
          color: var(--main-light);
          z-index: 1;
        }

        &.filled span {
          background-color: var(--accent-shade-1);
        }
      }

      .course-card {
        margin: 0;
      }

      a,
      .course-elective {
        position: relative;
        display: flex;
        align-items: center;
        background: var(--main-light);
        border-radius: 7px;
        padding: 10px;
        color: var(--main-dark-shade-0);
        transition: all var(--duration) ease-in;

        .image {
          position: relative;
          border-radius: 7px;
          overflow: hidden;
          width: 300px;
          height: 195px;
          flex: 0 0 auto;

          .course-image {
            display: block;
            max-width: 300px;
            object-fit: cover;
            object-position: 100%;
            width: 100%;
            height: 100%;
          }

          .course-technology {
            position: absolute;
            top: 10px;
            right: 15px;
            width: var(--spacing-36);
            filter: drop-shadow(0 2px 10px rgba(0, 12, 31, 0.5));
          }
        }

        .info {
          margin-left: var(--spacing-24);
          min-width: 0;

          .h5 {
            margin: 0;
            width: 80%;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
          }

          .instructors {
            margin-bottom: var(--spacing-4);
          }

          .course-rating {
            margin-bottom: var(--spacing-12);
          }

          .body-2 {
            color: var(--main-dark-shade-3);
            margin: 0 0 var(--spacing-12);
            padding-right: 50px;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            line-clamp: 2;
            -webkit-box-orient: vertical;
          }

          .props {
            display: flex;
            align-items: center;
            padding-left: 0;
            margin: 0;
            column-gap: 8px;

            li {
              list-style-type: none;
              line-height: 150%;

              &.dot {
                width: 4px;
                height: 4px;
                background: var(--main-dark);
                border-radius: 50%;
              }
            }
          }
        }

        .label {
          position: absolute;
          width: 92px;
          height: 28px;
          top: 10px;
          right: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 3px;
          background-color: rgba(84, 92, 105, 0.1);
          font-weight: 600;
          font-size: 14px;
          line-height: 14px;

          &.label-required {
            background-color: rgba(61, 175, 184, 0.2);
            color: var(--accent-shade-2);
          }

          &.label-elective {
            background-color: rgba(224, 178, 52, 0.2);
            color: var(--main-yellow-dark-4);
          }
        }

        &:not(.course-elective):hover {
          transform: scale(1.01);
        }
      }

      a {
        background: var(--main-light);
      }

      .course-elective {
        align-items: flex-start;

        .info {
          margin-left: 10px;
          padding-left: var(--spacing-24);
        }

        .elective-selector {
          margin: 0 0 var(--spacing-16);
        }
      }

      + article {
        margin-top: var(--spacing-16);
      }
    }
  }

  .track-line {
    background: var(--main-light-shade-3);
    position: absolute;
    height: 100%;
    top: 0;
    left: 13px;
    width: 2px;
    z-index: 0;

    .track-line-filler {
      background-color: var(--accent);
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 0;
      max-height: 100%;
      transition: all 500ms ease-out;
    }

    + * {
      z-index: 1;
    }
  }

  .track-long-description {
    line-height: 24px;
    color: var(--main-dark-light-4);
    word-break: break-word;
  }

  .track-badge {
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 10px;

    path {
      transition: all var(--duration) ease-in;

      &[fill] {
        fill: var(--main-light-shade-3);
      }

      &[stroke] {
        stroke: var(--main-light-shade-3);
      }
    }

    &.filled {
      path[fill] {
        fill: var(--accent);
      }

      path[stroke] {
        stroke: var(--accent);
      }
    }
  }

  &.lms {
    margin-top: var(--spacing-36);

    .holder {
      padding-bottom: var(--spacing-16);
    }

    .courses article {
      .course-elective {
        background-color: var(--main-light);
        filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));

        .image {
          width: 320px;
          height: 216px;

          img {
            max-width: unset;
          }
        }
      }
    }

    .elective-selector {
      width: 280px;
      margin-bottom: 8px;
    }

    .track-dot:before {
      background-color: var(--main-light-shade-1);
    }

    + .footer {
      padding-bottom: var(--spacing-120);

      .btn-primary {
        max-width: 224px;
      }
    }

    .course-elective .info .dropdown {
      margin-bottom: var(--spacing-16);
    }
  }
}

.footer {
  padding-left: var(--spacing-48);

  .h5 {
    margin: 0 0 var(--spacing-6);
  }

  .body-2 {
    margin: 0;
  }

  .btn-primary {
    margin-top: var(--spacing-16);
    padding-left: 0;
    padding-right: 0;
    width: 100%;
    max-width: 300px;
  }
}

@include bp($bp-laptop) {
  .list {
    .courses article .track-dot span {
      font-weight: 600;
      font-size: 16px;
      line-height: 140%;
      height: 24px;
      width: 24px;
    }

    &.lms {
      .list-title {
        font-size: 24px;
      }
    }
  }
}

@include bp($bp-mobile) {
  .list .courses article {
    a,
    .course-elective {
      .info {
        min-width: 0;
      }
    }

    .course-elective {
      padding: 0;
      flex-direction: column;
      max-width: 350px;
      border-radius: 10px;

      .image {
        position: relative;
        max-width: 350px;
        width: 100%;
        height: 0;
        padding-bottom: 66%;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;

        .course-image {
          position: absolute;
          max-width: 100%;
          width: 100%;
          height: 100%;
        }
      }

      .label {
        position: static;
        margin: var(--spacing-12) 0 var(--spacing-8) var(--spacing-24);
      }

      .info {
        margin-left: 0;
        padding: 0 var(--spacing-24) var(--spacing-20) var(--spacing-24);

        .body-2 {
          margin-top: 8px;
          padding-right: 0;
        }
      }
    }
  }

  .list.lms {
    ::v-deep .courses article {
      .course-elective {
        background-color: var(--main-light);
        filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));

        .image {
          width: 100%;
          height: auto;
        }
      }

      .course-card-container {
        a {
          filter: none;
        }

        .label {
          width: 92px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-top: var(--spacing-20);
          margin-bottom: 0;
        }

        .course-card-body {
          padding-top: var(--spacing-12);

          a {
            filter: none;
          }
        }
      }
    }
  }
}

@media screen and (max-width: 800px) {
  .list {
    .courses article {
      + article {
        margin-top: var(--spacing-48);
      }

      a,
      .course-elective {
        flex-direction: column;
        align-items: flex-start;
        max-width: 400px;

        .image {
          border-bottom-left-radius: 0;
          border-bottom-right-radius: 0;
          width: 100%;
          height: 0;
          padding-bottom: 65%;

          .course-image {
            max-width: 100%;
          }

          ~ * {
            padding-left: 20px;
            padding-right: 20px;
          }
        }

        .label {
          position: static;
          margin: 20px 0 16px 20px;
        }

        .info {
          margin-left: 0;

          .h5 {
            width: 100%;
            white-space: normal;
          }

          .body-2 {
            padding-right: 0;
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 3;
            overflow: hidden;
          }
        }
      }
    }

    .footer {
      .btn-primary {
        padding-left: 0;
        padding-right: 0;
        width: 100%;
        max-width: 350px;
      }
    }

    &.lms {
      .courses article {
        a,
        .course-elective {
          max-width: 350px;
        }

        .elective-selector {
          width: auto;
        }
      }
    }
  }
}

@include bp($bp-phone) {
  .list {
    .courses {
      article {
        padding-left: var(--spacing-48);

        a {
          ::v-deep .info {
            .h5 {
              margin-bottom: var(--spacing-4);
            }

            .course-rating {
              .rating {
                font-size: 16px;
                font-weight: 500;
              }
            }
          }
        }
      }
    }

    &.lms {
      + .footer {
        .btn-primary {
          max-width: 100%;
        }
      }
    }
  }
}

.fa {
  @import '../assets/sass/fa/components/list-courses.scss';
}
</style>
